// src/components/profile/ProfileEditDialog.tsx
//
// Owner-only profile editor. Fields:
//   • display_name      — free text, no uniqueness
//   • username          — claimed via /usernames/{lower} reservation
//   • is_account_private — toggle, takes effect immediately
//
// Privacy flip is Instagram-style: existing followers KEEP access
// when going public→private; only NEW follow attempts go through
// the request flow.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import { useT } from '../../i18n/useT';
import { PublicProfileFields, validateUsername } from '../../profile/types';
import { claimUsername } from '../../profile/username';

interface Props {
  open: boolean;
  onClose: () => void;
  currentProfile: PublicProfileFields;
}

export const ProfileEditDialog: React.FC<Props> = ({
  open,
  onClose,
  currentProfile,
}) => {
  const t = useT();
  const [displayName, setDisplayName] = useState(
    currentProfile.display_name ?? ''
  );
  const [username, setUsername] = useState(currentProfile.username ?? '');
  const [isPrivate, setIsPrivate] = useState(
    !!currentProfile.is_account_private
  );
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDisplayName(currentProfile.display_name ?? '');
      setUsername(currentProfile.username ?? '');
      setIsPrivate(!!currentProfile.is_account_private);
      setUsernameError(null);
      setTopError(null);
    }
  }, [open, currentProfile]);

  const handleUsernameBlur = () => {
    if (!username) {
      setUsernameError(null);
      return;
    }
    const v = validateUsername(username);
    if (v.ok !== true) {
      const failure = v as { ok: false; reason: string };
      setUsernameError(reasonToMessage(failure.reason, t));
    } else {
      setUsernameError(null);
    }
  };

  const handleSave = async () => {
    const me = auth.currentUser;
    if (!me) return;
    setSaving(true);
    setTopError(null);

    try {
      await setDoc(
        doc(db, 'users', me.uid),
        {
          display_name: displayName.trim() || null,
          is_account_private: isPrivate,
          updated_at: serverTimestamp(),
        },
        { merge: true }
      );

      const trimmedUsername = username.trim().toLowerCase();
      if (
        trimmedUsername &&
        trimmedUsername !== (currentProfile.username_lower || '')
      ) {
        const v = validateUsername(trimmedUsername);
        if (v.ok !== true) {
          const failure = v as { ok: false; reason: string };
          setUsernameError(reasonToMessage(failure.reason, t));
          setSaving(false);
          return;
        }
        const result = await claimUsername(trimmedUsername);
        if (result.ok !== true) {
          const fail = result as {
            ok: false;
            reason: string;
            detail?: string;
          };
          if (fail.reason === 'taken') {
            setUsernameError(t('That username is taken.'));
          } else if (fail.reason === 'too_soon') {
            setUsernameError(
              t('You can change your username again in {days} days.', {
                days: fail.detail || '?',
              })
            );
          } else if (fail.reason === 'invalid') {
            setUsernameError(reasonToMessage(fail.detail || '', t));
          } else {
            setUsernameError(t('Could not save username.'));
          }
          setSaving(false);
          return;
        }
      }

      onClose();
    } catch (err: any) {
      console.warn('Profile save failed:', err);
      setTopError(err?.message || t('Could not save changes.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('Edit profile')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('Display name')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            inputProps={{ maxLength: 60 }}
            disabled={saving}
            fullWidth
          />
          <TextField
            label={t('Username')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onBlur={handleUsernameBlur}
            error={!!usernameError}
            helperText={
              usernameError ||
              t('Letters, numbers, and underscores. 3–30 characters. Starts with a letter.')
            }
            inputProps={{ maxLength: 30 }}
            disabled={saving}
            fullWidth
            InputProps={{
              startAdornment: (
                <Typography
                  component="span"
                  sx={{ color: 'text.secondary', mr: 0.5 }}
                >
                  @
                </Typography>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                disabled={saving}
              />
            }
            label={t('Private account')}
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 5, mt: -1 }}>
            {t(
              'When private, new followers must be approved. Existing followers keep access.'
            )}
          </Typography>
          {topError && (
            <Typography color="error" variant="body2">
              {topError}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('Cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? t('Saving…') : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function reasonToMessage(reason: string, t: (s: string) => string): string {
  switch (reason) {
    case 'too_short':
      return t('Username is too short.');
    case 'too_long':
      return t('Username is too long.');
    case 'starts_with_digit':
      return t('Username must start with a letter.');
    case 'invalid_chars':
      return t('Username can contain only lowercase letters, numbers, and underscores.');
    default:
      return t('Username is invalid.');
  }
}
